/* global globalThis */
import React, {
  useContext,
  lazy,
  Suspense,
  useEffect,
  useState,
  useMemo,
} from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/core/styles';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { SignJWT } from 'jose';

import CommonLoader from '../Views/Common/CommonLoader';
import { Context } from 'context';
import {
  AUTHENTICATION_COMPLETE,
  DOMAIN_CONFIG_UPDATED,
  FRESHCHAT_INIT,
  UPDATE_SELECTED_COUNTRY,
  UPDATE_COUNTRY_CONFIG,
  SET_COUNTRY_RESOLVED,
} from 'context/actions';
import getDomainConfig from 'config/domain-config';
import getTheme from 'theme';
import Main from './Main/Main';
import AuthPortalRedirect from './AuthPortalRedirect';
import GlobalNotification from 'common-layout/GlobalNotification';
import Utils, { getCountryConfig } from 'Shared/Utils';
import { LOCATION_SERVICES, SUPPORTED_COUNTRIES_SERVICE } from 'Services';
import {
  AUTH_PORTAL_URLS,
  PULSE_PAY_PATIENT_PAYMENT_DOMAIN_PATTERN,
  PAGE_URLS,
} from './Main/constants';
import { isAuthPortalEnabled } from 'utils/auth-portal-url';
import ZendeskWidget from 'components/ZendeskWidget/ZendeskWidget';
import PylonWidget from 'components/PylonWidget/PylonWidget';
import {
  getSelectedCountry,
  setSelectedCountry,
  getRegionFromHostname,
  getRegionalOrigin,
  isGeolocationLandingDomain,
  isCountryManuallySelected,
} from 'utils';
import {
  applyMetadata,
  getDisplayName,
  getZendesk,
  syncPortal,
  waitForZendesk,
  ZENDESK_POST_LOGIN_METADATA_MS,
} from 'utils/zendesk-messenger-tags';

// lazy loading components.
const LoginComponent = lazy(() =>
  import('../Views/Login/LoginComponent/LoginComponent'),
);
const ForgotPassword = lazy(() =>
  import('../Views/ForgotPassword/ForgotPassword'),
);
const AUTH_PORTAL_ENABLED = isAuthPortalEnabled();

const AuthPortalForgotPassword = () => (
  <AuthPortalRedirect to={AUTH_PORTAL_URLS.FORGOT_PASSWORD} />
);
const AuthPortalLogin = () => <AuthPortalRedirect to={AUTH_PORTAL_URLS.LOGIN} />;

const ForgotPasswordRoute = AUTH_PORTAL_ENABLED
  ? AuthPortalForgotPassword
  : ForgotPassword;
const LoginRoute = AUTH_PORTAL_ENABLED ? AuthPortalLogin : LoginComponent;

const unauthenticatedReturnTo = () => {
  const pathName =
    (globalThis.location?.pathname ?? '/') + (globalThis.location?.search ?? '');
  const isReentrant =
    pathName === '/' ||
    pathName.startsWith(PAGE_URLS.LOGIN) ||
    pathName.startsWith(PAGE_URLS.LOGOUT);

  return isReentrant ? undefined : pathName;
};

const SSOCallback = lazy(() =>
  import('../Views/Login/SSOCallback/SSOCallback'),
);
const FlexPayPaymentRoot = lazy(() =>
  import('../Views/FlexPayV2/FlexPayPaymentRoot'),
);

const {
  REACT_APP_VYNE_MARKETING_PAGE,
  REACT_APP_ZENDESK_JWT_SIGNING_KEY,
  REACT_APP_ZENDESK_JWT_KID,
} = process.env;

function showMessengerAfterLoginMetadata(zE) {
  applyMetadata(() => {
    zE('messenger', 'show');
  });
}

function scheduleMessengerMetadataAfterLogin(zE, token) {
  zE('messenger', 'loginUser', (cbk) => {
    cbk(token);
    globalThis.setTimeout(
      () => showMessengerAfterLoginMetadata(zE),
      ZENDESK_POST_LOGIN_METADATA_MS,
    );
  });
}

async function authenticateZendeskMessengerUser(user) {
  const signingKey = REACT_APP_ZENDESK_JWT_SIGNING_KEY?.trim();
  const kid = REACT_APP_ZENDESK_JWT_KID?.trim();
  if (!signingKey || !kid) {
    console.warn('Zendesk JWT config is not configured');
    return;
  }

  const secretKey = new TextEncoder().encode(signingKey);
  const payload = {
    external_id: user.sub,
    name: getDisplayName(user.name || user.email || ''),
    email: user.email,
    email_verified: true,
    phone: user.phone_number,
    scope: 'user',
  };

  const token = await new SignJWT(payload)
    .setProtectedHeader({
      alg: 'HS256',
      typ: 'JWT',
      kid,
    })
    .sign(secretKey);

  const zE = getZendesk();
  if (!zE) {
    return;
  }

  scheduleMessengerMetadataAfterLogin(zE, token);
}

async function runZendeskSession(user, cancelled) {
  if (cancelled || !getZendesk()) {
    return;
  }

  if (!user) {
    applyMetadata();
    return;
  }

  try {
    await authenticateZendeskMessengerUser(user);
  } catch (err) {
    console.error('Zendesk messenger authentication failed', err);
  }
}

const Routes = () => {
  const {
    state: {
      user,
      isUserValidating,
      freshchat_initialised = false,
      activeTheme,
      countryConfig,
    },
    dispatch,
  } = useContext(Context);

  const [theme, setTheme] = useState(getTheme(activeTheme));
  const [initCount, setInitCount] = useState(0);
  // On the bare geolocation-landing domain (multi-region) we redirect to a
  // region domain before rendering, so the landing never serves app content.
  const [regionRedirecting, setRegionRedirecting] = useState(() =>
    isGeolocationLandingDomain(),
  );

  const { activeTheme: domainTheme, ...config } = getDomainConfig();

  const { pathname, search, hash } = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const initFreshChat = () => {
    if (!window.fcWidget) {
      setInitCount((prevVal) => prevVal + 1);
      return;
    }

    dispatch({
      type: FRESHCHAT_INIT,
    });

    window.fcWidget.init({
      token: 'e487c9b8-34c6-4bf8-bc22-0f026f05d7ec',
      host: 'https://subscribili-team.freshchat.com',
      externalId: user?.email,
      restoreId: user?.restoreId || null,
    });

    window.fcWidget.on('widget:loaded', function () {
      window.fcWidget.user.get(function (resp) {
        if (resp?.status !== 200) {
          const { name, email, phone_number, settings = {} } = user;

          const fullName = name.trim().split(' ');
          const [firstName = '', lastName = ''] = fullName;
          const role = user['custom:role']
            ? Utils.formatUserRole(user['custom:role'])
            : '';
          const level_name =
            settings?.level_data?.[`${user?.level}_name`] || '';

          window.fcWidget.user.create({
            firstName,
            lastName,
            email,
            phone: phone_number.slice(-10),
            phoneCountryCode: phone_number.slice(0, 2),
            meta: {
              role,
              level_name,
            },
          });

          window.fcWidget.on('user:created', function (resp) {
            let data = resp?.data;
            if (resp?.status === 200) {
              if (data.restoreId) {
                // TODO - Add this id to user db
              }
            }
          });
        }
      });
    });
  };

  // const MAX_RETRIES = 10;
  // useEffect(() => {
  //   let timeoutId = '';

  //   if(user) {
  //     if (freshchat_initialised || initCount >= MAX_RETRIES)
  //       clearTimeout(timeoutId);
  //     else
  //       timeoutId = setTimeout(() => {
  //         initFreshChat();
  //       }, 500*2);
  //   }
  // }, [user, initCount, freshchat_initialised]);

  // useEffect(() => {
  //   if (typeof (window.FreshworksWidget) === "function") {
  //     user
  //       ? window.FreshworksWidget("show")
  //       : window.FreshworksWidget("hide");
  //   }
  // }, [pathname, user]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapZendeskSession = async () => {
      try {
        await waitForZendesk();
        if (cancelled) return;

        syncPortal(pathname, () => {
          void runZendeskSession(user, cancelled);
        });
      } catch {
        /* snippet not loaded */
      }
    };

    void bootstrapZendeskSession();

    return () => {
      cancelled = true;
    };
  }, [user, pathname]);

  const fetchLocationByFacilityId = async (id) => {
    if (!id) return;

    try {
      const response = await LOCATION_SERVICES.getLocationByFacilityId(id);

      if (response?.type === 'success') {
        dispatch({
          type: AUTHENTICATION_COMPLETE,
          payload: { user: null },
        });
      } else {
        throw response;
      }
    } catch (err) {
      console.log(err);

      if (REACT_APP_VYNE_MARKETING_PAGE) {
        globalThis.location = REACT_APP_VYNE_MARKETING_PAGE;
      }
    }
  };

  useEffect(() => {
    // Bare app.subscribili.com in multi-region mode is a geolocation-only
    // landing — it must never serve app content, even for an authenticated
    // (localStorage) session. Route to the correct region domain first,
    // respecting a manual/stored selection, otherwise geolocating by IP.
    if (isGeolocationLandingDomain()) {
      const routeAwayFromLanding = async () => {
        let targetCountry = isCountryManuallySelected()
          ? getSelectedCountry()
          : null;
        if (!targetCountry) {
          const codes =
            await SUPPORTED_COUNTRIES_SERVICE.getSupportedCountryCodes();
          targetCountry = codes[0] || 'US';
        }
        const targetOrigin = getRegionalOrigin(targetCountry);
        if (targetOrigin && targetOrigin !== globalThis.location.origin) {
          // Build the destination on the trusted region origin (from the
          // getRegionalOrigin allowlist). Assigning the current path/query/hash
          // to a URL constructed from targetOrigin cannot change the host, so
          // the redirect target is not user-controlled.
          const dest = new URL(targetOrigin);
          dest.pathname = pathname;
          dest.search = search;
          dest.hash = hash;
          globalThis.location.replace(dest.toString());
          return;
        }
        // Defensive: no region origin to route to — render normally.
        setRegionRedirecting(false);
      };
      void routeAwayFromLanding();
      return;
    }

    // Domain takes priority over stored country — ensures returning users on
    // app.uk.* don't get auto-logged in with tokens from a previous US session.
    const domainCountry = getRegionFromHostname();
    if (domainCountry) {
      setSelectedCountry(domainCountry);
      dispatch({ type: UPDATE_SELECTED_COUNTRY, payload: { selectedCountry: domainCountry } });
      dispatch({ type: UPDATE_COUNTRY_CONFIG, payload: { countryConfig: getCountryConfig(domainCountry) } });
      dispatch({ type: SET_COUNTRY_RESOLVED });
    }

    const selectedCountry = domainCountry || getSelectedCountry();
    const userDetails = localStorage.getItem(`userDetail_${selectedCountry}`);
    if (userDetails) {
      const getUserDetails = () => {
        const updatedDetails = JSON.parse(userDetails);

        dispatch({
          type: AUTHENTICATION_COMPLETE,
          payload: { user: updatedDetails },
        });
      };

      getUserDetails();
    } else {
      const noPath = pathname === '/';
      const facilityId = searchParams.get('facid');

      if (noPath && facilityId) {
        fetchLocationByFacilityId(facilityId);
      } else {
        dispatch({
          type: AUTHENTICATION_COMPLETE,
          payload: { user: null },
        });
      }

      getZendesk()?.('messenger', 'logoutUser');
    }

    dispatch({
      type: DOMAIN_CONFIG_UPDATED,
      payload: { domainConfig: config, activeTheme: domainTheme },
    });
  }, []);

  useEffect(() => {
    setTheme(getTheme(activeTheme));
  }, [activeTheme]);

  // Show only a loader while the geolocation-landing redirect resolves, so the
  // bare domain never flashes app content before routing to the region domain.
  if (regionRedirecting) {
    return <CommonLoader />;
  }

  const hostname = globalThis.location?.hostname ?? '';
  const isPatientPayDomain = PULSE_PAY_PATIENT_PAYMENT_DOMAIN_PATTERN.test(hostname);

  const handleRedirectURL = () => {
    const pathName =
      (globalThis.location?.pathname ?? '/') + (globalThis.location?.search ?? '');
    if (
      pathName !== '/' &&
      pathName !== PAGE_URLS.LOGIN &&
      pathName !== PAGE_URLS.LOGOUT
    ) {
      return PAGE_URLS.LOGIN + '?redirect=' + encodeURIComponent(pathName);
    } else {
      return PAGE_URLS.LOGIN;
    }
  };

  const unauthenticatedFallback = AUTH_PORTAL_ENABLED ? (
    <AuthPortalRedirect
      to={AUTH_PORTAL_URLS.LOGIN}
      returnTo={unauthenticatedReturnTo()}
    />
  ) : (
    <Redirect to={handleRedirectURL()} />
  );

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<CommonLoader></CommonLoader>}>
        {isPatientPayDomain ? (
          <Switch>
            <Route
              path={PAGE_URLS.PULSE_PAY_INVOICES_PAY}
              component={FlexPayPaymentRoot}
            />
          </Switch>
        ) : (
          <>
            {user ? (
              <Switch>
                {/* Must precede the catch-all so SSO login works even with an active session */}
                <Route exact path={PAGE_URLS.SSO_CALLBACK} component={SSOCallback} />
                <Route path="/" component={Main} />
              </Switch>
            ) : (
              <Switch>
                {/* FLEX Patient pay route */}
                <Route
                  path={PAGE_URLS.FORGOT_PASSWORD}
                  exact
                  component={ForgotPasswordRoute}
                />
                <Route
                  exact
                  path={PAGE_URLS.LOGIN}
                  component={LoginRoute}
                />
                <Route
                  exact
                  path={PAGE_URLS.SSO_CALLBACK}
                  component={SSOCallback}
                />
                {isUserValidating ? (
                  <></>
                ) : (
                  unauthenticatedFallback
                )}
              </Switch>
            )}
          </>
        )}
      </Suspense>
      <GlobalNotification />
      {countryConfig?.pylonAppId ? <PylonWidget /> : <ZendeskWidget />}
      <CssBaseline />
    </ThemeProvider>
  );
};

export default Routes;
